/**
 * Speed Wallet mini-app integration — pure helpers.
 *
 * When AurumRun is opened as a Speed Wallet mini app, Speed appends five query
 * params to the URL (https://speed-wallet.gitbook.io/speed-wallet-mini-apps/data-sharing):
 *
 *   acct      the user's Speed Wallet account id (e.g. `acct_li8hh2xyRuSBWnE4`)
 *   lang      the language the user set inside Speed (`en`, `es`, `de`, `hi`, ...)
 *   bal_btc   available BTC balance in sats            (ignored)
 *   bal_usdt  available USDT balance                   (ignored)
 *   p_add     the user's Lightning address (`abc@speed.app`)
 *
 * We use them for three things:
 *   1. `acct` marks the visit as a Speed Wallet lead — the referral is credited to
 *      the Speed Wallet affiliate account, whose code `useSpeedWallet` fetches
 *      from the backend (resolved from the admin-configured affiliate email).
 *   2. `lang` switches the UI locale (`useSpeedWallet`).
 *   3. `p_add` is remembered for a future "withdraw to Speed Wallet" flow — it is
 *      not used anywhere yet, only persisted.
 *
 * Everything here is side-effect free apart from the explicit localStorage
 * read/write helpers, so the query parsing and locale mapping are unit-testable
 * without a Nuxt context. Persistence is localStorage rather than a cookie: the
 * values are only ever read by the client (deposit ordering, a future withdraw
 * prefill) and must survive the referral cookie being cleared at signup.
 */
import { DEFAULT_LOCALE, SUPPORTED_CODES } from '#layers/base/i18n/locales'
import { normalizeLocale } from '#layers/base/i18n/normalizeLocale'

export const SPEED_WALLET_ACCT_PARAM = 'acct'
export const SPEED_WALLET_LANG_PARAM = 'lang'
export const SPEED_WALLET_ADDRESS_PARAM = 'p_add'

export const SPEED_WALLET_ACCT_KEY = 'speed_wallet:acct'
export const SPEED_WALLET_ADDRESS_KEY = 'speed_wallet:address'
export const SPEED_WALLET_SESSION_KEY = 'speed_wallet:session'

/** Speed account ids look like `acct_li8hh2xyRuSBWnE4`; be lenient on the exact
 *  shape but reject anything that is not a plain token, so a crafted URL cannot
 *  write arbitrary strings into storage. */
const ACCT_RE = /^[A-Za-z0-9_-]{1,128}$/
/** Lightning address = `name@domain`; loose on purpose, it is only stored. */
const LN_ADDRESS_RE = /^[^\s@]{1,64}@[^\s@]{1,190}$/

export interface SpeedWalletParams {
  acct: string
  lang: string | null
  address: string | null
}

export interface SpeedWalletContext {
  acct: string | null
  address: string | null
}

/** First value of a possibly-repeated query param, or null. Mirrors `firstQuery` in useReferral. */
function first(v: unknown): string | null {
  const s = Array.isArray(v) ? v[0] : v
  return typeof s === 'string' && s.length > 0 ? s : null
}

/**
 * Read the Speed Wallet params off a route query. Returns null unless a valid
 * `acct` is present — `lang` / `p_add` on their own are not a Speed visit.
 */
export function parseSpeedWalletParams(query: Record<string, unknown>): SpeedWalletParams | null {
  const acct = first(query[SPEED_WALLET_ACCT_PARAM])
  if (!acct || !ACCT_RE.test(acct)) return null

  const rawAddress = first(query[SPEED_WALLET_ADDRESS_PARAM])
  const address = rawAddress && LN_ADDRESS_RE.test(rawAddress) ? rawAddress : null

  return { acct, lang: first(query[SPEED_WALLET_LANG_PARAM]), address }
}

/**
 * Map Speed's `lang` onto one of our locales, or null when we do not support
 * that language at all. Speed sends languages we do not have (e.g. `hi`); for
 * those we must NOT touch the locale — `normalizeLocale` would silently fall
 * back to English and override a language the user picked in our own switcher.
 */
export function speedWalletLocale(lang: string | null | undefined): string | null {
  if (!lang) return null
  const base = lang.trim().toLowerCase().split('-')[0]
  if (!base) return null
  const supported = SUPPORTED_CODES.some((c) => c.toLowerCase().split('-')[0] === base)
  if (!supported) return null
  const code = normalizeLocale(lang)
  // Belt and braces: a supported base language always normalizes to a real
  // code, but never hand back the default because of a fallback.
  return code === DEFAULT_LOCALE && base !== DEFAULT_LOCALE ? null : code
}

function storage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null
    return window.localStorage
  } catch {
    // Storage access itself can throw (privacy mode, sandboxed iframes).
    return null
  }
}

function sessionStore(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null
    return window.sessionStorage
  } catch {
    return null
  }
}

/** Persist the Speed context. `p_add` is only overwritten when present, so a
 *  later visit that omits it does not forget the address. */
export function persistSpeedWalletContext(params: SpeedWalletParams): void {
  // Session-scoped: this tab IS the mini app, while the localStorage context below
  // outlives it and only says the browser once arrived from Speed.
  try {
    sessionStore()?.setItem(SPEED_WALLET_SESSION_KEY, '1')
  } catch {
    // Same tolerance as the localStorage writes below.
  }
  const ls = storage()
  if (!ls) return
  try {
    ls.setItem(SPEED_WALLET_ACCT_KEY, params.acct)
    if (params.address) ls.setItem(SPEED_WALLET_ADDRESS_KEY, params.address)
  } catch {
    // Quota / privacy errors are not worth surfacing — the integration degrades
    // to "no Speed context", which is the same as a plain browser visit.
  }
}

export function readSpeedWalletContext(): SpeedWalletContext {
  const ls = storage()
  if (!ls) return { acct: null, address: null }
  try {
    return {
      acct: ls.getItem(SPEED_WALLET_ACCT_KEY) || null,
      address: ls.getItem(SPEED_WALLET_ADDRESS_KEY) || null,
    }
  } catch {
    return { acct: null, address: null }
  }
}

/** True once this browser has opened the app from inside Speed Wallet. */
export function isSpeedWalletUser(): boolean {
  return readSpeedWalletContext().acct !== null
}

/**
 * True while this tab is running as the Speed Wallet mini app. Speed opens it with
 * `acct` on the URL, so that param marks the arrival and the session flag carries it
 * across SPA navigation. Scoped to the tab on purpose: a later visit in an ordinary
 * browser must not be treated as the mini app.
 */
export function isSpeedMiniAppSession(): boolean {
  if (typeof window === 'undefined') return false
  const acct = new URLSearchParams(window.location.search).get(SPEED_WALLET_ACCT_PARAM)
  if (acct && ACCT_RE.test(acct)) return true
  return sessionStore()?.getItem(SPEED_WALLET_SESSION_KEY) === '1'
}

/** The account id a payment request must carry. Storage can be unavailable inside
 *  the webview, so fall back to the `acct` the mini-app URL still holds. */
export function speedWalletAccountId(): string | null {
  const stored = readSpeedWalletContext().acct
  if (stored) return stored
  if (typeof window === 'undefined') return null
  const acct = new URLSearchParams(window.location.search).get(SPEED_WALLET_ACCT_PARAM)
  return acct && ACCT_RE.test(acct) ? acct : null
}

/** The remembered Lightning address (`p_add`), for a future withdraw prefill. */
export function getSpeedWalletAddress(): string | null {
  return readSpeedWalletContext().address
}

/**
 * Deposit-method order for Speed Wallet users: the Lightning rail (which is
 * Speed on the processor side) goes first among the crypto methods. The relative
 * order of everything else is untouched, and a list without `lightning` (region
 * config not resolved, or the rail kill-switched) comes back as-is.
 */
export function orderForSpeedWallet<T extends string>(methods: readonly T[]): T[] {
  const idx = methods.indexOf('lightning' as T)
  if (idx <= 0) return [...methods]
  return [methods[idx]!, ...methods.slice(0, idx), ...methods.slice(idx + 1)]
}
